'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { HEADER_PADDING_BOTTOM, HEADER_PADDING_TOP, INPUT_SM_HEIGHT, LAYOUT_MAIN_PADDING_TOP, LAYOUT_PADDING_X } from 'HubStyleTokens/sizes';
import { TEMPLATE_SECTION_DIVIDER_BORDER_COLOR } from 'HubStyleTokens/theme';
import PropTypes from 'prop-types';
import { cloneElement, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { HEADING_SIZES } from '../text/HeadingConstants';
import { warnIfFragment } from '../utils/devWarnings';
import UIButtonWrapper from './UIButtonWrapper'; // 100% arbitrary cutoff for widthConstrained mode

const CONSTRAINED_TITLE_WIDTH = 544;
const pseudoBorderMixin = css(["&::after{content:' ';position:absolute;left:", ";bottom:0;width:calc(100% - ", " * 2);height:1px;background-color:", ";}"], LAYOUT_PADDING_X, LAYOUT_PADDING_X, TEMPLATE_SECTION_DIVIDER_BORDER_COLOR);
const Outer = styled.header.withConfig({
  displayName: "UIHeader__Outer",
  componentId: "sc-16mz7d5-0"
})(["display:flex;position:relative;flex-direction:column;margin-bottom:", ";padding-bottom:", ";padding-left:", ";padding-right:", ";padding-top:", ";", ";"], ({
  condensed
}) => condensed ? '0' : LAYOUT_MAIN_PADDING_TOP, ({
  tabs
}) => tabs ? '0' : HEADER_PADDING_BOTTOM, LAYOUT_PADDING_X, LAYOUT_PADDING_X, HEADER_PADDING_TOP, ({
  condensed,
  fullWidth,
  tabs
}) => {
  if (condensed || tabs) return null;
  return fullWidth ? `border-bottom: 1px solid ${TEMPLATE_SECTION_DIVIDER_BORDER_COLOR}` : pseudoBorderMixin;
});
const Inner = styled.div.withConfig({
  displayName: "UIHeader__Inner",
  componentId: "sc-16mz7d5-1"
})(["display:flex;align-items:center;flex-wrap:wrap;min-height:", ";padding-bottom:", ";"], INPUT_SM_HEIGHT, ({
  tabs
}) => tabs && '8px');
const Alert = styled.div.withConfig({
  displayName: "UIHeader__Alert",
  componentId: "sc-16mz7d5-2"
})(["margin-top:20px;margin-bottom:", ";"], ({
  tabs
}) => tabs && '20px');
const Toolbar = styled(UIButtonWrapper).attrs({
  justify: 'end'
}).withConfig({
  displayName: "UIHeader__Toolbar",
  componentId: "sc-16mz7d5-3"
})(["flex-grow:1;margin-left:auto;"]);

const renderTitle = (title, badge) => {
  if (!badge) return title;
  const badgeOutput = /*#__PURE__*/cloneElement(badge, {
    className: classNames(badge.props.className, 'private-header__badge')
  });
  return /*#__PURE__*/_jsxs("span", {
    className: "private-header__title__inner",
    children: [title, badgeOutput]
  });
};

const renderHeading = (badge, children, headingLevel, title, titleControls) => {
  const titleControlsWrapper = titleControls && /*#__PURE__*/_jsx("div", {
    className: "private-header__title-badge",
    children: titleControls
  });

  const titleWrapperClassName = 'private-header__title' + (headingLevel === 'h1' ? " private-page__title" : "");
  const Heading = headingLevel;
  return /*#__PURE__*/_jsxs("div", {
    className: titleWrapperClassName,
    children: [/*#__PURE__*/_jsx(Heading, {
      className: 'private-header__heading' + (!children && !titleControls ? " private-header__heading--solo" : ""),
      children: renderTitle(title, badge)
    }), titleControlsWrapper]
  });
};

const renderToolbar = children => {
  return children && /*#__PURE__*/_jsx(Toolbar, {
    className: "private-header__toolbar",
    children: children
  });
};
/**
 * Standard page title which allows CTAs, filters, etc.
 */


export default function UIHeader(props) {
  const {
    alert,
    badge,
    breadcrumbs,
    children,
    className,
    details,
    flush,
    fullWidth,
    headingLevel,
    role,
    tabs,
    title,
    titleControls,
    use
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["alert", "badge", "breadcrumbs", "children", "className", "details", "flush", "fullWidth", "headingLevel", "role", "tabs", "title", "titleControls", "use"]);

  const [widthConstrained, setWidthConstrained] = useState(false);
  const headerRef = useRef(null);
  useEffect(() => {
    const measureWidth = () => {
      const headerEl = headerRef.current;
      const titleEl = headerEl && headerEl.querySelector('.private-header__title');
      if (!titleEl) return; // Measure width without the width-constrained class

      const hasWidthConstrainedClass = headerEl.classList.contains('private-header--width-constrained');

      if (hasWidthConstrainedClass) {
        headerEl.classList.remove('private-header--width-constrained');
      }

      const newWidthConstrained = titleEl.clientWidth < CONSTRAINED_TITLE_WIDTH;

      if (hasWidthConstrainedClass) {
        headerEl.classList.add('private-header--width-constrained');
      }

      setWidthConstrained(newWidthConstrained);
    };

    measureWidth();
    addEventListener('resize', measureWidth);
    return () => {
      removeEventListener('resize', measureWidth);
    };
  }, []);
  const classes = classNames('private-header', className, fullWidth && 'private-header--full-width', flush && 'private-header--flush', details && 'private-header--with-details', tabs && 'private-header--with-tabs', widthConstrained && 'private-header--width-constrained');
  warnIfFragment(badge, UIHeader.displayName, 'badge');
  return /*#__PURE__*/_jsxs(Outer, Object.assign({
    className: classes,
    ref: headerRef,
    role: "presentation",
    breadcrumbs: breadcrumbs,
    condensed: use === 'condensed',
    fullWidth: fullWidth,
    tabs: tabs
  }, rest, {
    children: [breadcrumbs, /*#__PURE__*/_jsxs(Inner, {
      className: "private-header__inner",
      role: role,
      tabs: tabs,
      children: [title && renderHeading(badge, children, headingLevel, title, titleControls), renderToolbar(children)]
    }), details, alert && /*#__PURE__*/_jsx(Alert, {
      tabs: tabs,
      children: alert
    }), tabs]
  }));
}
UIHeader.propTypes = {
  alert: PropTypes.node,
  breadcrumbs: PropTypes.node,
  badge: PropTypes.node,
  children: PropTypes.node,
  details: PropTypes.node,
  flush: PropTypes.bool.isRequired,
  fullWidth: PropTypes.bool.isRequired,
  headingLevel: PropTypes.oneOf(HEADING_SIZES),
  tabs: PropTypes.node,
  title: PropTypes.node,
  titleControls: PropTypes.node,
  use: PropTypes.oneOf(['condensed'])
};
UIHeader.defaultProps = {
  flush: false,
  // for backwards compat, this will be true in next major version
  fullWidth: true,
  headingLevel: 'h1',
  role: 'banner'
};
UIHeader.displayName = 'UIHeader';